import React, { useCallback, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import classes from './style.module.scss';
import useTranslation from '~/app/hooks/useTranslation';
import { WorkOrderStatuses, WorkOrderTaskIssueStatus, WorkOrderTaskServiceStatus, WorkOrderTaskUserList } from '~/workorder/types';
import Icon from '~/app/common/Icon';
import { formatTaskHoursLogDate, getWorkOrderTaskUserTotalHours } from './helper';
import { useStoreActions, useStoreState } from '~/store';
import { IssueOrServiceType } from '~/app/data/statuses/chat';
import ConfirmationMenu from '~/app/common/ConfirmationMenu';
import Dropdown from '~/app/common/Dropdown';
import { notify } from '~/app/helpers/notify';
import TimeEntry, { TimeEntryMode } from './TimeEntry';

export type UsersComponent = React.FC<{ users: WorkOrderTaskUserList[], taskStatus: WorkOrderTaskIssueStatus | WorkOrderTaskServiceStatus }>;

type TimeEntryModalState = {
    open: boolean;
    mode: TimeEntryMode;
    entry?: WorkOrderTaskUserList;
};

const Users: UsersComponent = ({ users, taskStatus }) => {
    const { t } = useTranslation('workorder');
    const { t: tParts } = useTranslation('parts');
    const { id, taskId, type } = useParams<{ id: string; taskId: string; type: string }>();

    const workOrderStatus = useStoreState(state => state.workOrder.data.status);
    const workOrderId = useStoreState(state => state.workOrder.data.id);
    const getWorkOrderTaskDetail = useStoreActions(actions => actions.workOrder.getWorkOrderTaskDetail);
    const deleteWorkOrderTimeEntry = useStoreActions(actions => actions.workOrder.deleteWorkOrderTimeEntry);

    const typeParam = type === 'issue' ? IssueOrServiceType.ISSUE : IssueOrServiceType.SERVICE;
    const numericTaskId = Number(taskId);

    const [timeEntryModal, setTimeEntryModal] = useState<TimeEntryModalState>({ open: false, mode: 'add' });
    const [deleteTargetId, setDeleteTargetId] = useState<number | undefined>();
    const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);

    const totalHours = useMemo(
        () => users.reduce((sum, user) => sum + getWorkOrderTaskUserTotalHours(user), 0),
        [users],
    );

    const refreshTaskDetail = useCallback(async () => {
        await getWorkOrderTaskDetail({
            id: Number(id),
            taskId: numericTaskId,
            type: typeParam,
        });
    }, [getWorkOrderTaskDetail, id, numericTaskId, typeParam]);

    const closeTimeEntry = useCallback(() => {
        setTimeEntryModal({ open: false, mode: 'add' });
    }, []);

    const openAddModal = useCallback(() => {
        setTimeEntryModal({ open: true, mode: 'add' });
    }, []);

    const openEditModal = useCallback((entry: WorkOrderTaskUserList) => {
        setTimeEntryModal({ open: true, mode: 'edit', entry });
    }, []);

    const onDeleteConfirm = useCallback((entry: WorkOrderTaskUserList) => {
        setDeleteTargetId(entry.id);
        setIsDeleteConfirmOpen(true);
    }, []);

    const cancelDelete = useCallback(() => {
        setIsDeleteConfirmOpen(false);
        setDeleteTargetId(undefined);
    }, []);

    const confirmDelete = useCallback(() => {
        if (deleteTargetId == null) {
            cancelDelete();
            return;
        }
        const entryId = deleteTargetId;
        cancelDelete();
        deleteWorkOrderTimeEntry(entryId).then(data => {
            if (data.success) {
                notify(t('detail.timeEntryDeleted'), 'success');
                refreshTaskDetail();
            }
        });
    }, [deleteTargetId, cancelDelete, deleteWorkOrderTimeEntry, t, refreshTaskDetail]);

    const showActions = useMemo(() => {
        const isIssue = type === 'issue';
        return !(
            workOrderStatus === WorkOrderStatuses.APPROVED ||
            workOrderStatus === WorkOrderStatuses.DELAYED ||
            workOrderStatus === WorkOrderStatuses.ONHOLD ||
            (isIssue && taskStatus !== WorkOrderTaskIssueStatus.DURING_WORK)
        );
    }, [taskStatus, workOrderStatus, type]);

    return (
        <div className={classes['card']}>
            <div className={classes['section-header']}>
                <h2 className={classes['section-header-title']}>
                    <Icon name="schedule" size={20} className={classes['section-header-icon']} />
                    {t('detail.taskTimeRegistrations')}
                </h2>
                {showActions && (
                    <button type="button" className={classes['add-action']} onClick={openAddModal}>
                        + {tParts('add')}
                    </button>
                )}
            </div>
            {users.length === 0 ? (
                <p className={classes.empty}>{t('detail.noTimeRegistrations')}</p>
            ) : (
                <>
                    <div className={classes.list}>
                        {users.map(user => {
                            const hours = getWorkOrderTaskUserTotalHours(user);
                            const logDate = formatTaskHoursLogDate(user.hoursLogDate);
                            return (
                                <div key={user.id} className={classes['list-row']}>
                                    <div className={classes['row-main']}>
                                        <p className={classes['row-name']}>{user.name || t('detail.system')}</p>
                                        {logDate && <p className={classes['row-meta']}>{logDate}</p>}
                                    </div>
                                    <div className={classes['row-actions']}>
                                        <span className={classes['row-value']}>
                                            {t('detail.taskHoursShort', { hours })}
                                        </span>
                                        {showActions && (
                                            <Dropdown icon={null} position="bottom left" closeOnClick>
                                                <Dropdown.Trigger>
                                                    <button type="button" className={classes['row-menu-trigger']} aria-label="Actions">
                                                        <Icon name="more_vert" size={20} />
                                                    </button>
                                                </Dropdown.Trigger>
                                                <Dropdown.Content>
                                                    <ul className={classes['row-menu']}>
                                                        <li onClick={() => openEditModal(user)}>
                                                            <Icon className={classes['row-menu-icon']} name="edit" />
                                                            {t('detail.timeEntryEdit')}
                                                        </li>
                                                        <li className={classes['row-menu-delete']} onClick={() => onDeleteConfirm(user)}>
                                                            <Icon className={classes['row-menu-icon']} name="delete" />
                                                            {t('detail.timeEntryDelete')}
                                                        </li>
                                                    </ul>
                                                </Dropdown.Content>
                                            </Dropdown>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                    <div className={classes['total-bar']}>
                        <span>{t('detail.taskTimeTotal')}</span>
                        <span className={classes['total-value']}>
                            {t('detail.taskTimeTotalHours', { hours: totalHours })}
                        </span>
                    </div>
                </>
            )}

            <TimeEntry
                open={timeEntryModal.open}
                mode={timeEntryModal.mode}
                workOrderId={workOrderId}
                taskId={numericTaskId}
                type={typeParam}
                initialEntry={timeEntryModal.entry}
                onClose={closeTimeEntry}
                onSaved={refreshTaskDetail}
            />

            <ConfirmationMenu
                isOpen={isDeleteConfirmOpen}
                onConfirm={confirmDelete}
                onCancel={cancelDelete}
                confirmationText={t('detail.timeEntryDeleteConfirm')}
            />
        </div>
    );
}

export default Users;